import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../components/Icon';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../config/api';
import useUnreadMessages from '../hooks/useUnreadMessages';
import Chat from '../components/Chat'; 
import { toast } from '../components/ToastProvider';
import '../styles/Dashboard.css';
import '../styles/SalonDashboard.css';
import '../styles/AdminDashboard.css';
import '../styles/B2BTrainings.css';
import B2BPanel from '../components/B2BPanel';
import EmployeesPanel from '../components/EmployeesPanel';
import SalonProfilePanel from '../components/SalonProfilePanel';
import NotificationsPanel, { useNotifications } from '../components/NotificationsPanel';

const normalizeBooking = (b) => ({
  ...b,
  date:      (b.booking_date || b.date || '').slice(0, 10),
  time:      (b.booking_time || b.time || '').slice(0, 5),
  total:     Number(b.total) || 0,
  firstName: b.first_name || b.firstName || '',
  lastName:  b.last_name || b.lastName || '',
  userId:    b.user_id || b.userId,
  salon:     b.salon || { name: b.salon_name },
  employee:  b.employee || (b.employee_name ? { name: b.employee_name } : null),
  services:  b.services || (b.service_names?.filter(Boolean).map(name => ({ name })) || []),
  products:  b.products || [],
});

const SalonDashboard = () => {
  const { user } = useAuth();
  const { unreadBreakdown } = useUnreadMessages(user);
  const { unread: notifUnread } = useNotifications(user?.id);
  const location = useLocation();
  const clientUnread = Object.keys(unreadBreakdown || {})
    .filter(key => key.startsWith('client_'))
    .reduce((sum, key) => sum + (unreadBreakdown[key] || 0), 0);
  
  const adminUnread = Object.keys(unreadBreakdown || {})
    .filter(key => key.includes('_admin'))
    .reduce((sum, key) => sum + (unreadBreakdown[key] || 0), 0);
  
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedChatClient, setSelectedChatClient] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectBookingId, setRejectBookingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const handleChatDeleted = () => {
      setSelectedChatClient(null);
    };
    window.addEventListener('chat-deleted', handleChatDeleted);
    return () => window.removeEventListener('chat-deleted', handleChatDeleted);
  }, []);

  useEffect(() => {
    if (location.state?.openTab) {
      setActiveTab(location.state.openTab);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    const handler = (e) => setActiveTab(e.detail.tab);
    window.addEventListener('hairkus-open-tab', handler);
    return () => window.removeEventListener('hairkus-open-tab', handler);
  }, []);

  const loadBookings = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiFetch('/bookings');
      setBookings((data || []).map(normalizeBooking).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch {
    }
  }, [user]);

  useEffect(() => { 
    loadBookings(); 
  }, [loadBookings]);

  useEffect(() => { 
    let f = [...bookings];
    if (filter === 'pending') f = f.filter(b => b.status === 'pending');
    else if (filter === 'confirmed') f = f.filter(b => b.status === 'confirmed');
    else if (filter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      f = f.filter(b => b.date === today);
    }
    if (selectedDate) f = f.filter(b => b.date === selectedDate);
    
    setFilteredBookings(f);
  }, [bookings, filter, selectedDate]);

  const updateStatus = async (bookingId, status, notes = '') => {
    try {
      await apiFetch(`/bookings/${bookingId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, notes }),
      });
      loadBookings();
    } catch {
      toast.error('Błąd podczas aktualizacji statusu');
    }
  };

  const handleAccept   = (id) => { updateStatus(id, 'confirmed'); toast.success('Rezerwacja zaakceptowana!'); };
  const handleReject   = (id) => { 
    setRejectBookingId(id);
    setRejectReason('');
    setRejectModalOpen(true);
  };
  const handleRejectConfirm = () => {
    if (rejectBookingId) {
      updateStatus(rejectBookingId, 'rejected', rejectReason);
      setRejectModalOpen(false);
      setRejectBookingId(null);
      setRejectReason('');
    }
  };
  const handleComplete = (id) => { updateStatus(id, 'completed'); };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    today: bookings.filter(b => b.date === new Date().toISOString().split('T')[0]).length,
    revenue: bookings.filter(b => ['confirmed','completed'].includes(b.status))
      .reduce((s, b) => s + (b.total || 0), 0),
  };

  const clientsWhoBooked = [...new Map(
    bookings.map(b => [
      b.email, 
      { 
        email: b.email, 
        userId: b.user_id || b.userId,
        firstName: b.first_name || b.firstName || '',
        lastName: b.last_name || b.lastName || '',
      }
    ])
  ).values()];

  const getStatusBadge = (status) => {
    const map = {
      pending:   { text: 'Oczekuje',     color: '#f59e0b', icon: 'clock'     },
      confirmed: { text: 'Potwierdzona', color: '#10b981', icon: 'confirmed' },
      rejected:  { text: 'Odrzucona',   color: '#ef4444', icon: 'rejected'  },
      completed: { text: 'Zrealizowana', color: '#6366f1', icon: 'check'     },
      cancelled: { text: 'Anulowana',   color: '#9ca3af', icon: 'cancelled' },
    };
    const b = map[status] || map.pending;
    return (
      <span className="status-badge" style={{ background: b.color + '20', color: b.color }}>
        <Icon name={b.icon} style={{marginRight:'5px'}} />{b.text}
      </span>
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">

        <div className="dashboard-header">
          <h1>Panel Salonu</h1>
          <p>Witaj, {user?.salonName || user?.firstName}! <Icon name="scissors" style={{marginRight:'7px'}} /></p>
        </div>

        {/* Statystyki */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon"><Icon name="stats" style={{marginRight:'7px'}} /></div>
            <div className="stat-content"><h3>{stats.total}</h3><p>Wszystkie</p></div>
          </div>
          <div className="stat-card highlight-pending">
            <div className="stat-icon"><Icon name="pending" color="#f59e0b" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{stats.pending}</h3><p>Do akceptacji</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Icon name="confirmed" color="#10b981" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{stats.confirmed}</h3><p>Potwierdzone</p></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Icon name="calendar" style={{marginRight:'7px'}} /></div>
            <div className="stat-content"><h3>{stats.today}</h3><p>Dzisiaj</p></div>
          </div>
          <div className="stat-card highlight-revenue">
            <div className="stat-icon"><Icon name="money" style={{marginRight:'5px'}} /></div>
            <div className="stat-content"><h3>{stats.revenue} zł</h3><p>Przychód</p></div>
          </div>
        </div>

        {/* Zakładki */}
        <div className="admin-tabs">
          <button className={`admin-tab ${activeTab === 'bookings' ? 'active' : ''}`} onClick={() => setActiveTab('bookings')}>
            <Icon name="calendar" style={{marginRight:'7px'}} /> Rezerwacje
          </button>
          <button className={`admin-tab ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')} style={{position:'relative'}}>
            <Icon name="chat" style={{marginRight:'7px'}} /> Wiadomości od klientów
            {activeTab !== 'clients' && clientUnread > 0 && (
              <span className="tab-unread-badge">{clientUnread > 9 ? '9+' : clientUnread}</span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'employees' ? 'active' : ''}`} onClick={() => setActiveTab('employees')}>
            <Icon name="users" style={{marginRight:'7px'}} />Pracownicy
          </button>
          <button className={`admin-tab ${activeTab === 'b2b' ? 'active' : ''}`} onClick={() => setActiveTab('b2b')}>
            <Icon name="package" style={{marginRight:'7px'}} />Zamówienia B2B
          </button>
          <button className={`admin-tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
            <Icon name="salon" style={{marginRight:'7px'}} /> Profil salonu
          </button>
          <button className={`admin-tab ${activeTab === 'admin' ? 'active' : ''}`} onClick={() => setActiveTab('admin')} style={{position:'relative'}}>
            <Icon name="settings" style={{marginRight:'7px'}} /> Kontakt z adminem
            {activeTab !== 'admin' && adminUnread > 0 && (
              <span className="tab-unread-badge">{adminUnread > 9 ? '9+' : adminUnread}</span>
            )}
          </button>
          <button className={`admin-tab ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveTab('notifications')} style={{position:'relative'}}>
            <Icon name="notification" style={{marginRight:'7px'}} />Powiadomienia
            {activeTab !== 'notifications' && notifUnread > 0 && (
              <span className="tab-unread-badge">{notifUnread > 9 ? '9+' : notifUnread}</span>
            )}
          </button>
        </div>

        <div className="dashboard-content-single">

          {/* Rezerwacje */}
          {activeTab === 'bookings' && (
            <div className="dashboard-card-full">
              <div className="card-header-with-filters">
                <h2><Icon name="calendar" style={{marginRight:'7px'}} /> Zarządzanie rezerwacjami</h2>
                <div className="filters-row">
                  <div className="filter-buttons">
                    <button className={`filter-btn ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
                      <Icon name="clock" style={{marginRight:'5px'}} />Oczekujące ({stats.pending})
                    </button>
                    <button className={`filter-btn ${filter === 'confirmed' ? 'active' : ''}`} onClick={() => setFilter('confirmed')}>
                      <Icon name="confirmed" style={{marginRight:'5px'}} />Potwierdzone
                    </button>
                    <button className={`filter-btn ${filter === 'today' ? 'active' : ''}`} onClick={() => setFilter('today')}>
                      <Icon name="calendar-day" style={{marginRight:'5px'}} />Dzisiaj
                    </button>
                    <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                      <Icon name="list" style={{marginRight:'5px'}} />Wszystkie
                    </button>
                  </div>
                  <input
                    type="date"
                    className="date-filter"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                  />
                </div>
              </div>

              {filteredBookings.length > 0 ? (
                <div className="salon-bookings-list">
                  {filteredBookings.map(booking => (
                    <div key={booking.id} className="salon-booking-card">
                      <div className="booking-card-header">
                        <div className="booking-card-date">
                          <span className="date-large"><Icon name="calendar" style={{marginRight:'7px'}} /> {booking.date}</span>
                          <span className="time-large"><Icon name="clock" style={{marginRight:"4px"}} /> {booking.time}</span>
                        </div>
                        {getStatusBadge(booking.status)}
                      </div>

                      <div className="booking-card-content">
                        <div className="booking-info-row">
                          <div className="info-block">
                            <label>Klient:</label>
                            <p><strong>{booking.email}</strong></p>
                            <p><Icon name="phone" style={{marginRight:'5px'}} /> {booking.phone}</p>
                          </div>
                          <div className="info-block">
                            <label>Pracownik:</label>
                            <p><Icon name="user" style={{marginRight:'7px'}} /> <strong>{booking.employee?.name}</strong></p>
                            <p className="small-text">{booking.employee?.specialization}</p>
                          </div>
                          <div className="info-block">
                            <label>Czas trwania:</label>
                            <p><Icon name="timer" style={{marginRight:"4px"}} /> <strong>{booking.duration} min</strong></p>
                          </div>
                        </div>

                        <div className="booking-info-row">
                          <div className="info-block full-width">
                            <label>Usługi:</label>
                            <div className="tags-list">
                              {booking.services?.map((s, idx) => (
                                <span key={idx} className="service-tag-small">{s.name}</span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {booking.products?.length > 0 && (
                          <div className="booking-info-row">
                            <div className="info-block full-width">
                              <label>Produkty do odbioru:</label>
                              <div className="tags-list">
                                {booking.products.map((p, idx) => (
                                  <span key={idx} className="product-tag-small"> {p.name} - {p.price} zł</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="booking-card-footer">
                          <div className="booking-total-price">
                            <strong>Kwota: {booking.total} zł</strong>
                            {booking.productsTotal > 0 && (
                              <small>(usługi: {booking.servicesTotal} zł + produkty: {booking.productsTotal} zł)</small>
                            )}
                          </div>
                          <div className="booking-actions">
                            {booking.status === 'pending' && (
                              <>
                                <button className="action-btn accept-btn" onClick={() => handleAccept(booking.id)}>
                                  <Icon name="check" style={{marginRight:'6px'}} />Akceptuj
                                </button>
                                <button className="action-btn reject-btn" onClick={() => handleReject(booking.id)}>
                                  <Icon name="close" style={{marginRight:'6px'}} />Odrzuć
                                </button>
                              </>
                            )}
                            {booking.status === 'confirmed' && (
                              <button className="action-btn complete-btn" onClick={() => handleComplete(booking.id)}>
                                <Icon name="star" style={{marginRight:'6px'}} />Zrealizowana
                              </button>
                            )}
                            {['pending', 'confirmed'].includes(booking.status) && (
                              <button
                                className="action-btn"
                                style={{ background: '#e0e0e0', color: '#666' }}
                                onClick={() => {
                                  setSelectedChatClient({ email: booking.email, userId: booking.userId });
                                  setActiveTab('clients');
                                }}
                              >
                                <Icon name="chat" style={{marginRight:'7px'}} /> Napisz
                              </button>
                            )}
                            {booking.status === 'rejected' && <span className="rejected-info">Rezerwacja odrzucona</span>}
                            {booking.status === 'completed' && <span className="completed-info"><Icon name="check" style={{marginRight:"4px"}} /> Wizyta zrealizowana</span>}
                          </div>
                        </div>
                      </div>

                      <div className="booking-card-meta">
                        Utworzona: {new Date(booking.created_at || booking.createdAt).toLocaleString('pl-PL')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="calendar" style={{marginRight:'7px'}} /></div>
                  <h3>Brak rezerwacji</h3>
                  <p>{filter === 'pending' ? 'Nie ma oczekujących rezerwacji' : 'Brak rezerwacji dla wybranego filtra'}</p>
                </div>
              )}
            </div>
          )}

          {/* Wiadomości od klientów */}
          {activeTab === 'clients' && (
            <div className="dashboard-card-full">
              <h2><Icon name="chat" style={{marginRight:'7px'}} /> Wiadomości od klientów</h2>
              <div className="chat-tabs">
                {clientsWhoBooked.map((client, idx) => {
                  const convKey = `client_${client.userId}_salon_${(user?.salonName || '').replace(/\s+/g, '_')}`;
                  const isActive = selectedChatClient?.email === client.email;
                  const unread = unreadBreakdown[convKey] || 0;
                  const hasUnread = unread > 0 && !isActive;
                  
                  return (
                    <button
                      key={idx}
                      className={`chat-tab ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedChatClient(client)}
                      style={{
                        position:'relative',
                        fontWeight: hasUnread ? '700' : 'normal'
                      }}
                    >
                      <Icon name="user" style={{marginRight:'7px'}} /> {client.email}
                      {hasUnread && <span className="chat-tab-unread-dot" />}
                    </button>
                  );
                })}
              </div>
              {selectedChatClient ? (
                <Chat
                  conversationId={`client_${selectedChatClient.userId}_salon_${(user?.salonName || '').replace(/\s+/g, '_')}`}
                  recipientName={`${selectedChatClient.firstName || ''} ${selectedChatClient.lastName || ''}`.trim() || selectedChatClient.email}
                  recipientRole="Klient"
                />
              ) : (
                <div className="empty-state">
                  <div className="empty-icon"><Icon name="chat" style={{marginRight:'7px'}} /></div>
                  <h3>Wybierz klienta</h3>
                  <p>Kliknij klienta powyżej lub użyj przycisku "Napisz" przy rezerwacji</p>
                </div>
              )}
            </div>
          )}

          {/* Pracownicy */}
          {activeTab === 'employees' && (
            <div className="dashboard-card-full">
              <EmployeesPanel user={user} />
            </div>
          )}

          {/* Zamówienia B2B */}
          {activeTab === 'b2b' && (
            <div className="dashboard-card-full">
              <B2BPanel user={user} approvedSalons={[{salonName: user?.salonName}]} />
            </div>
          )}

          {/* Powiadomienia */}
          {activeTab === 'notifications' && (
            <div className="dashboard-card-full">
              <NotificationsPanel user={user} />
            </div>
          )}

          {/* Profil salonu */}
          {activeTab === 'profile' && (
            <div className="dashboard-content-single">
              <SalonProfilePanel user={user} />
            </div>
          )}

          {/* Kontakt z administratorem */}
          {activeTab === 'admin' && (
            <div className="dashboard-card-full">
              <h2><Icon name="settings" style={{marginRight:'7px'}} /> Kontakt z Administratorem</h2>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Pytania do administracji sieci HAIRKUS? Napisz tutaj.
              </p>
              <Chat
                conversationId={`salon_${user?.id}_admin`}
                recipientName="Administrator HAIRKUS"
                recipientRole="admin"
              />
            </div>
          )}

        </div>
      </div>
      
      {/* Powód odrzucenia */}
      {rejectModalOpen && (
        <div className="modal-overlay" onClick={() => setRejectModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Odrzuć rezerwację</h3>
            <p style={{color: '#6b7280', marginBottom: '20px'}}>Podaj powód odrzucenia (opcjonalnie):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="np. Termin już zajęty, brak dostępności fryzjera..."
              rows="4"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '15px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
              <button
                onClick={handleRejectConfirm}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Odrzuć rezerwację
              </button>
              <button
                onClick={() => setRejectModalOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalonDashboard;